/**
 * Unified Action Generator
 *
 * Replaces 5 duplicate action generation implementations across:
 * - curve-editor.tsx: interpolateCurve()
 * - script-creator.tsx: generateActions() + interpolateBlockActions()
 * - vertical-editor.tsx: interpolateBlockActions()
 * - block-editor.tsx: generateFunscript() + generateBlockActions()
 * - manual-control-grid.tsx: generateFunscript() + smoothActions()
 */

import type { FunscriptAction, ScriptPattern, ScriptAxis } from "./script-store"
import type { ControlPoint } from "@/components/editors/bezier-curve-canvas"
import {
  controlModeHandler,
  type ControlMode,
  type RecordedAction,
} from "./control-mode-handler"

/**
 * Evaluates a cubic bezier's position at a given absolute time, where the curve is defined by
 * four (time, position) control points — mirrors the exact bezierCurveTo() shape used to draw
 * the curve in bezier-curve-canvas.tsx, just in raw time/position units instead of canvas pixels.
 * The bezier's time-component isn't linear in the curve parameter `u`, so we binary-search for
 * the `u` whose time matches `targetTime` (monotonic for well-formed control points) and
 * evaluate position at that same `u`.
 */
function bezierPositionAtTime(
  t0: number, p0: number,
  t1: number, p1: number,
  t2: number, p2: number,
  t3: number, p3: number,
  targetTime: number,
): number {
  const cubic = (a: number, b: number, c: number, d: number, u: number) => {
    const inv = 1 - u
    return inv * inv * inv * a + 3 * inv * inv * u * b + 3 * inv * u * u * c + u * u * u * d
  }

  let lo = 0
  let hi = 1
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2
    if (cubic(t0, t1, t2, t3, mid) < targetTime) lo = mid
    else hi = mid
  }
  return cubic(p0, p1, p2, p3, (lo + hi) / 2)
}

export class ActionGenerator {
  // ─── Curve Actions ─────────────────────────────────────────────

  /**
   * Generate actions from Bezier curve control points.
   * Port of `interpolateCurve()` from curve-editor.tsx.
   *
   * Used by: curve-editor, script-creator (curve mode)
   */
  fromCurve(
    controlPoints: ControlPoint[],
    sampleInterval: number = 100,
    minPositionChange: number = 5
  ): FunscriptAction[] {
    if (!Array.isArray(controlPoints)) return []
    const sortedPoints = [...controlPoints].sort((a, b) => a.time - b.time)
    if (sortedPoints.length < 2) return []

    // For linear easing (the default), control points ARE the keyframes.
    // The Handy firmware interpolates smoothly between them — sampling in between
    // just adds redundant commands that interrupt the motion and cause stuttering.
    const allLinear = sortedPoints.every(p => !p.easing || p.easing === "linear")
    if (allLinear) {
      const out: FunscriptAction[] = []
      for (const p of sortedPoints) {
        const pos = Math.round(Math.max(0, Math.min(100, p.position)))
        const last = out[out.length - 1]
        // Only emit if position changed meaningfully vs previous keyframe
        if (!last || Math.abs(pos - last.pos) >= minPositionChange || p === sortedPoints[sortedPoints.length - 1]) {
          out.push({ at: Math.round(p.time), pos })
        }
      }
      return out
    }

    // Non-linear easing: sample the ACTUAL bezier shape drawn on the curve editor canvas
    // (bezier-curve-canvas.tsx), not a straight-line blend between keyframe values. Previously
    // this interpolated position linearly regardless of easing/handles/smoothing, so the
    // exported script — and anything driven by it, like the video-overlay follow dot — silently
    // diverged from the curve actually drawn on screen for every eased or smoothed segment.
    const actions: FunscriptAction[] = []
    actions.push({
      at: sortedPoints[0].time,
      pos: Math.round(Math.max(0, Math.min(100, sortedPoints[0].position))),
    })

    let lastSampledPos = sortedPoints[0].position
    let lastSampledTime = sortedPoints[0].time

    for (let i = 0; i < sortedPoints.length - 1; i++) {
      const startPoint = sortedPoints[i]
      const endPoint = sortedPoints[i + 1]
      const duration = endPoint.time - startPoint.time
      const isLastSegment = i === sortedPoints.length - 2

      if (duration > 0) {
        // Matches the canvas's own condition for drawing a straight line vs a bezier curve.
        const isSegmentLinear = startPoint.easing === "linear" && !startPoint.handleOut && !endPoint.handleIn

        let cp1Time = startPoint.time, cp1Pos = startPoint.position
        let cp2Time = endPoint.time, cp2Pos = endPoint.position

        if (!isSegmentLinear) {
          if (startPoint.handleOut) {
            // Handles are authored as on-screen pixel offsets, which have no exact time/position
            // equivalent independent of the canvas zoom level at the moment they were dragged.
            // There's no canonical conversion, so the offset is taken at roughly 1:1 (pixels as
            // ms / position-units) — far closer to the drawn shape than ignoring it outright.
            cp1Time = startPoint.time + startPoint.handleOut.x
            cp1Pos = startPoint.position + startPoint.handleOut.y
          } else {
            const smoothing = (startPoint.smoothing ?? 50) / 100
            cp1Time = startPoint.time + duration * 0.5 * smoothing
            cp1Pos = startPoint.position
          }

          if (endPoint.handleIn) {
            cp2Time = endPoint.time + endPoint.handleIn.x
            cp2Pos = endPoint.position + endPoint.handleIn.y
          } else {
            const smoothing = (endPoint.smoothing ?? 50) / 100
            cp2Time = endPoint.time - duration * 0.5 * smoothing
            cp2Pos = endPoint.position
          }
        }

        for (let t = startPoint.time + sampleInterval; t < endPoint.time; t += sampleInterval) {
          const rawPos = isSegmentLinear
            ? startPoint.position + ((t - startPoint.time) / duration) * (endPoint.position - startPoint.position)
            : bezierPositionAtTime(
                startPoint.time, startPoint.position,
                cp1Time, cp1Pos,
                cp2Time, cp2Pos,
                endPoint.time, endPoint.position,
                t,
              )
          const pos = Math.round(Math.max(0, Math.min(100, rawPos)))
          if (Math.abs(pos - lastSampledPos) >= minPositionChange && t - lastSampledTime >= 100) {
            actions.push({ at: Math.round(t), pos })
            lastSampledPos = pos
            lastSampledTime = t
          }
        }
      }

      // Always keep the final keyframe of the whole curve, even if its position is close to the
      // last sample — otherwise the script (and the overlay dot) can stop just short of where
      // the curve actually ends.
      const endPos = Math.round(Math.max(0, Math.min(100, endPoint.position)))
      if (Math.abs(endPos - lastSampledPos) >= minPositionChange || isLastSegment) {
        actions.push({ at: endPoint.time, pos: endPos })
        lastSampledPos = endPos
        lastSampledTime = endPoint.time
      }
    }

    return actions
  }

  // ─── Block Actions (timeline blocks with control points) ──────

  /**
   * Generate actions from a single block's control points mapped to
   * an absolute time range [startTime, endTime].
   * Port of `interpolateBlockActions()` from script-creator.tsx and vertical-editor.tsx.
   *
   * Used by: script-creator (vertical mode), vertical-editor
   */
  fromBlocks(
    controlPoints: ControlPoint[],
    startTime: number,
    endTime: number,
    sampleInterval: number = 100
  ): FunscriptAction[] {
    const sortedPoints = [...controlPoints].sort((a, b) => a.time - b.time)
    if (sortedPoints.length < 2) return []

    const duration = endTime - startTime
    const totalPointTime = sortedPoints[sortedPoints.length - 1].time - sortedPoints[0].time

    // Map control points directly to absolute time — no intermediate sampling.
    // The device interpolates linearly between keyframes; redundant intermediate
    // points cause micro-pauses in the motion.
    const actions: FunscriptAction[] = []
    let lastPos: number | null = null

    for (const point of sortedPoints) {
      const absoluteTime = totalPointTime > 0
        ? startTime + (point.time / sortedPoints[sortedPoints.length - 1].time) * duration
        : startTime
      const pos = Math.round(Math.max(0, Math.min(100, point.position)))

      if (lastPos === null || Math.abs(pos - lastPos) >= 3) {
        actions.push({ at: Math.round(absoluteTime), pos })
        lastPos = pos
      }
    }

    return actions
  }

  // ─── Curve Actions (script-creator generateActions style) ─────

  /**
   * Generate actions by sampling control points over an absolute duration.
   * Port of `generateActions()` from script-creator.tsx.
   * Uses a simpler interpolation (find surrounding points, linear interp).
   *
   * Used by: script-creator (curve mode)
   */
  fromCurveSampled(
    controlPoints: ControlPoint[],
    duration: number,
    sampleInterval: number = 100
  ): FunscriptAction[] {
    // Delegate to fromCurve which now handles linear easing correctly
    // (outputs control points directly without redundant intermediate samples)
    if (controlPoints.length >= 2) {
      return this.fromCurve(controlPoints, sampleInterval, 5)
    }

    // Fallback: single point or empty — just cap to duration
    if (controlPoints.length === 1) {
      return [{ at: 0, pos: Math.round(Math.max(0, Math.min(100, controlPoints[0].position))) }]
    }
    return []
  }

  // ─── Recording Actions ────────────────────────────────────────

  /**
   * Generate a complete funscript object from recorded manual control actions.
   * Applies optional smoothing, then converts via ControlModeHandler.
   * Port of `generateFunscript()` + `smoothActions()` from manual-control-grid.tsx.
   *
   * Used by: manual-control-grid
   */
  fromRecording(
    recordedActions: RecordedAction[],
    controlMode: ControlMode,
    smoothingEnabled: boolean = true,
    smoothingAmount: number = 30
  ): FunscriptAction[] {
    let actions = recordedActions

    if (smoothingEnabled && smoothingAmount > 0 && actions.length >= 3) {
      actions = this.smoothRecordedActions(actions, smoothingAmount)
    }

    return controlModeHandler.generateFunscriptActions(controlMode, actions)
  }

  /**
   * Smooth recorded actions with configurable amount.
   * Port of `smoothActions()` from manual-control-grid.tsx.
   */
  smoothRecordedActions(
    actions: RecordedAction[],
    smoothingAmount: number
  ): RecordedAction[] {
    const windowSize = Math.max(2, Math.floor(smoothingAmount / 10))
    const smoothed: RecordedAction[] = []

    for (let i = 0; i < actions.length; i++) {
      const windowStart = Math.max(0, i - windowSize)
      const windowEnd = Math.min(actions.length - 1, i + windowSize)

      let sumX = 0
      let sumY = 0
      let count = 0
      for (let j = windowStart; j <= windowEnd; j++) {
        sumX += actions[j].x
        sumY += actions[j].y
        count++
      }

      smoothed.push({
        time: actions[i].time,
        x: sumX / count,
        y: sumY / count,
        velocity: actions[i].velocity,
        zoneValues: actions[i].zoneValues,
      })
    }

    return smoothed
  }

  // ─── Pattern Block Actions ────────────────────────────────────

  /**
   * Generate a complete funscript from pattern blocks.
   * Port of `generateFunscript()` from block-editor.tsx.
   *
   * Used by: block-editor (full script generation)
   */
  fromPatternBlocks(
    blocks: Array<{
      pattern: ScriptPattern & { customActions?: FunscriptAction[] }
      duration: number
      repeatCount: number
      speedMultiplier: number
      intensityMultiplier: number
      strokeLengthMultiplier: number
    }>
  ): FunscriptAction[] {
    const actions: FunscriptAction[] = []
    let currentTime = 0

    blocks.forEach((block) => {
      const { pattern, duration, repeatCount, speedMultiplier, intensityMultiplier, strokeLengthMultiplier } = block
      const blockDuration = duration * repeatCount * 1000

      if (pattern.customActions && pattern.customActions.length > 0) {
        const patternDuration =
          pattern.customActions[pattern.customActions.length - 1].at
        for (let repeat = 0; repeat < repeatCount; repeat++) {
          pattern.customActions.forEach((action) => {
            const scaledTime =
              (action.at / patternDuration) * duration * 1000
            actions.push({
              at: Math.round(
                currentTime + repeat * duration * 1000 + scaledTime
              ),
              pos: Math.round(action.pos * intensityMultiplier),
            })
          })
        }
      } else {
        const cycleTime = 1000 / (pattern.baseSpeed * speedMultiplier)
        const maxPos = Math.min(
          100,
          pattern.strokeLength * strokeLengthMultiplier
        )
        const minPos = Math.max(0, 100 - maxPos)

        for (let t = 0; t < blockDuration; t += cycleTime / 2) {
          const isUp = Math.floor(t / (cycleTime / 2)) % 2 === 0
          let pos: number

          switch (pattern.type) {
            case "sine":
              pos = isUp ? maxPos : minPos
              break
            case "square":
              pos = isUp ? maxPos : minPos
              break
            case "sawtooth": {
              const progress = (t % cycleTime) / cycleTime
              pos = minPos + (maxPos - minPos) * progress
              break
            }
            default:
              pos = isUp ? maxPos : minPos
          }

          actions.push({
            at: Math.round(currentTime + t),
            pos: Math.round(pos),
          })
        }
      }

      currentTime += blockDuration
    })

    return actions
  }

  /**
   * Generate preview actions for a single pattern block.
   * Port of `generateBlockActions()` from block-editor.tsx.
   *
   * Used by: block-editor (single block preview)
   */
  generateBlockPreview(block: {
    pattern: ScriptPattern & { customActions?: FunscriptAction[] }
    duration: number
    repeatCount: number
    speedMultiplier: number
    intensityMultiplier: number
    strokeLengthMultiplier: number
  }): FunscriptAction[] {
    const actions: FunscriptAction[] = []
    const blockDuration = block.duration * 1000

    if (
      block.pattern.customActions &&
      block.pattern.customActions.length > 0
    ) {
      const originalDuration = Math.max(
        ...block.pattern.customActions.map((a) => a.at)
      )
      return block.pattern.customActions.map((action) => ({
        at: Math.round(
          (action.at / originalDuration) *
            blockDuration *
            block.speedMultiplier
        ),
        pos: Math.round(
          Math.max(
            0,
            Math.min(
              100,
              action.pos *
                block.intensityMultiplier *
                (block.strokeLengthMultiplier / 100)
            )
          )
        ),
      }))
    }

    const timeStep = 100
    for (
      let time = 0;
      time <= blockDuration * block.repeatCount;
      time += timeStep
    ) {
      const t = time / 1000
      let position = 50

      switch (block.pattern.type) {
        case "sine":
          position =
            50 +
            50 *
              Math.sin(
                (t *
                  block.pattern.baseSpeed *
                  block.speedMultiplier *
                  Math.PI *
                  2) /
                  1
              )
          break
        case "square":
          position =
            (Math.floor(
              (t * block.pattern.baseSpeed * block.speedMultiplier * 2) % 2
            ) === 0
              ? 100
              : 0) *
            (block.pattern.intensity / 100)
          break
        case "sawtooth":
          position =
            ((t * block.pattern.baseSpeed * block.speedMultiplier * 100) %
              100) *
            (block.pattern.intensity / 100)
          break
        case "custom":
          if (block.pattern.id === "random-chaos") {
            position = Math.random() * 100
          } else {
            position =
              50 +
              30 *
                Math.sin(
                  (t *
                    block.pattern.baseSpeed *
                    block.speedMultiplier *
                    Math.PI *
                    2) /
                    1
                )
          }
          break
      }

      position =
        50 +
        (position - 50) *
          (block.pattern.intensity / 100) *
          block.intensityMultiplier
      position = position * (block.strokeLengthMultiplier / 100)
      position = Math.max(0, Math.min(100, position))

      actions.push({ at: Math.round(time), pos: Math.round(position) })
    }

    return actions
  }

  // ─── Multi-Axis Helpers ───────────────────────────────────────

  /**
   * Interpolate a value from a set of control points at a given time.
   * Shared utility used by all editors and the merge function.
   */
  interpolateAtTime(points: ControlPoint[], timeMs: number): number | undefined {
    if (!points || points.length < 2) return undefined
    const sorted = [...points].sort((a, b) => a.time - b.time)
    if (timeMs <= sorted[0].time) return sorted[0].position
    if (timeMs >= sorted[sorted.length - 1].time) return sorted[sorted.length - 1].position
    for (let i = 0; i < sorted.length - 1; i++) {
      if (timeMs >= sorted[i].time && timeMs <= sorted[i + 1].time) {
        const t = (timeMs - sorted[i].time) / (sorted[i + 1].time - sorted[i].time)
        return sorted[i].position + t * (sorted[i + 1].position - sorted[i].position)
      }
    }
    return undefined
  }

  /**
   * Merge axis layer control points into an existing set of stroke actions.
   *
   * For each action in strokeActions, looks up the value of each axis at
   * that timestamp and adds it to the action. Existing `pos` (stroke) is
   * never modified.
   *
   * @param strokeActions - The base stroke actions (from fromCurve, fromBlocks, etc.)
   * @param axisLayers - Map of axis name → control points for that axis
   * @returns New array of FunscriptAction with all axis fields populated
   */
  mergeAxisLayers(
    strokeActions: FunscriptAction[],
    axisLayers: Partial<Record<ScriptAxis, ControlPoint[]>>
  ): FunscriptAction[] {
    const axes = Object.keys(axisLayers) as ScriptAxis[]
    if (axes.length === 0) return strokeActions

    return strokeActions.map(action => {
      const merged: FunscriptAction = { ...action }
      for (const axis of axes) {
        const pts = axisLayers[axis]
        if (!pts || pts.length < 2) continue
        const value = this.interpolateAtTime(pts, action.at)
        if (value !== undefined) {
          (merged as any)[axis] = Math.round(Math.max(0, Math.min(100, value)))
        }
      }
      return merged
    })
  }

}

export const actionGenerator = new ActionGenerator()

// ─── Multi-Axis Helpers ───────────────────────────────────────

/**
 * Interpolate a value from a set of control points at a given time.
 * Shared utility used by all editors and the merge function.
 */
function interpolateAtTime(points: ControlPoint[], timeMs: number): number | undefined {
  if (!points || points.length < 2) return undefined
  const sorted = [...points].sort((a, b) => a.time - b.time)
  if (timeMs <= sorted[0].time) return sorted[0].position
  if (timeMs >= sorted[sorted.length - 1].time) return sorted[sorted.length - 1].position
  for (let i = 0; i < sorted.length - 1; i++) {
    if (timeMs >= sorted[i].time && timeMs <= sorted[i + 1].time) {
      const t = (timeMs - sorted[i].time) / (sorted[i + 1].time - sorted[i].time)
      return sorted[i].position + t * (sorted[i + 1].position - sorted[i].position)
    }
  }
  return undefined
}

function interpolateActionValue(actions: FunscriptAction[], timeMs: number, axis: keyof FunscriptAction): number | undefined {
  const sorted = [...actions]
    .filter((action) => typeof action[axis] === "number")
    .sort((a, b) => a.at - b.at)
  if (sorted.length === 0) return undefined
  if (timeMs <= sorted[0].at) return Number(sorted[0][axis])
  if (timeMs >= sorted[sorted.length - 1].at) return Number(sorted[sorted.length - 1][axis])
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i]
    const b = sorted[i + 1]
    if (timeMs >= a.at && timeMs <= b.at) {
      const av = Number(a[axis])
      const bv = Number(b[axis])
      const t = (timeMs - a.at) / Math.max(1, b.at - a.at)
      return av + t * (bv - av)
    }
  }
  return undefined
}

/**
 * Merge axis layer control points into an existing set of stroke actions.
 *
 * For each action in strokeActions, looks up the value of each axis at
 * that timestamp and adds it to the action. Existing `pos` (stroke) is
 * never modified.
 *
 * @param strokeActions - The base stroke actions (from fromCurve, fromBlocks, etc.)
 * @param axisLayers - Map of axis name → control points for that axis
 * @returns New array of FunscriptAction with all axis fields populated
 */
export function mergeAxisLayers(
  strokeActions: FunscriptAction[],
  axisLayers: Partial<Record<ScriptAxis, ControlPoint[]>>
): FunscriptAction[] {
  const axes = Object.keys(axisLayers) as ScriptAxis[]
  if (axes.length === 0) return strokeActions

  const times = new Set<number>(strokeActions.map((action) => action.at))
  for (const axis of axes) {
    const pts = axisLayers[axis]
    if (!pts || pts.length < 2) continue
    pts.forEach((point) => times.add(Math.round(point.time)))
  }

  return Array.from(times).sort((a, b) => a - b).map(time => {
    const existing = strokeActions.find((action) => action.at === time)
    const basePos = interpolateActionValue(strokeActions, time, "pos")
    const merged: FunscriptAction = {
      ...(existing ?? {}),
      at: time,
      pos: Math.round(Math.max(0, Math.min(100, basePos ?? existing?.pos ?? 50))),
    }
    for (const axis of axes) {
      const pts = axisLayers[axis]
      if (!pts || pts.length < 2) continue
      const value = interpolateAtTime(pts, time)
      if (value !== undefined) {
        (merged as any)[axis] = Math.round(Math.max(0, Math.min(100, value)))
      }
    }
    return merged
  })
}

export function mergeBlockAxisLayers(
  strokeActions: FunscriptAction[],
  axisLayers: Partial<Record<ScriptAxis, ControlPoint[]>>,
  startTime: number,
  endTime: number,
): FunscriptAction[] {
  const absoluteLayers: Partial<Record<ScriptAxis, ControlPoint[]>> = {}
  for (const axis of Object.keys(axisLayers) as ScriptAxis[]) {
    const pts = axisLayers[axis]
    if (!pts || pts.length < 2) continue
    const axisActions = actionGenerator.fromBlocks(pts, startTime, endTime)
    if (axisActions.length < 2) continue
    absoluteLayers[axis] = axisActions.map((action) => ({
      id: `${axis}-${action.at}`,
      time: action.at,
      position: action.pos,
      easing: "linear" as const,
      smoothing: 0,
    }))
  }
  return mergeAxisLayers(strokeActions, absoluteLayers)
}
